import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { motion, AnimatePresence, type Variants } from 'motion/react'
import {
  usePersonNotes, useCreatePersonNote, useDeletePersonNote,
  type PersonNote, type NoteKind, NOTE_KIND_LABEL, NOTE_KIND_CLASS,
} from '../../api/notesApi'
import { Button, TextArea, Dropdown, EmptyState } from '../../../../shared/ui'
import { HrSection } from '../primitives/HrSection'
import type { HrPersonOutletContext } from '../HrPersonPage'

const listVariants: Variants = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.05, delayChildren: 0.04 } },
}
const noteVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.32, ease: 'easeOut' } },
  exit:   { opacity: 0, x: -16, height: 0, marginTop: 0, marginBottom: 0, transition: { duration: 0.22, ease: 'easeIn' } },
}

const DATE_FMT = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })

const KIND_OPTIONS = [
  { value: 'general', label: 'Allgemein' },
  { value: 'tax',     label: 'Steuerbüro' },
  { value: 'health',  label: 'Gesundheit' },
] as const

export default function NotizenTab() {
  const { mainId } = useOutletContext<HrPersonOutletContext>()
  const { data: notes = [], isLoading } = usePersonNotes(mainId)
  const createNote = useCreatePersonNote(mainId)
  const deleteNote = useDeletePersonNote(mainId)

  const [composing, setComposing] = useState(false)
  const [text, setText] = useState('')
  const [kind, setKind] = useState<NoteKind>('general')

  const sorted = [...notes].sort((a, b) => b.createdAt.localeCompare(a.createdAt))

  function handleSubmit() {
    const t = text.trim()
    if (!t) return
    createNote.mutate(
      { kind, text: t },
      {
        onSuccess: () => {
          setText('')
          setKind('general')
          setComposing(false)
        },
      },
    )
  }

  return (
    <HrSection
      title="Notizen"
      subtitle="hr.PersonNote · Steuerbüro · Gesundheit · Allgemein"
      actions={
        !composing ? (
          <Button onClick={() => setComposing(true)} size="md" variant="primary">
            <span className="material-symbols-outlined text-base mr-1" aria-hidden="true">edit_note</span>
            Neue Notiz
          </Button>
        ) : null
      }
    >
      <div className="space-y-4">
        {/* Compose pane — animated expand/collapse */}
        <AnimatePresence initial={false}>
          {composing ? (
            <motion.div
              key="compose"
              initial={{ opacity: 0, height: 0, y: -8 }}
              animate={{ opacity: 1, height: 'auto', y: 0, transition: { duration: 0.28, ease: 'easeOut' } }}
              exit={{ opacity: 0, height: 0, y: -8, transition: { duration: 0.18, ease: 'easeIn' } }}
              className="overflow-hidden"
            >
              <div className="rounded-2xl border border-outline-variant/40 bg-surface-container-lowest p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <label className="text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                    Art
                  </label>
                  <Dropdown
                    value={kind}
                    onChange={v => setKind(v as NoteKind)}
                    options={KIND_OPTIONS as unknown as { value: string; label: string }[]}
                    size="compact"
                  />
                </div>
                <TextArea
                  value={text}
                  onChange={e => setText(e.target.value)}
                  placeholder="Notiz eingeben…"
                  rows={4}
                  autoFocus
                />
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => { setComposing(false); setText(''); setKind('general') }}
                    disabled={createNote.isPending}
                  >
                    Abbrechen
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={!text.trim() || createNote.isPending}
                  >
                    {createNote.isPending ? 'Speichert…' : 'Speichern'}
                  </Button>
                </div>
                {createNote.isError ? (
                  <p className="text-xs text-rose-600">
                    Fehler: {(createNote.error as Error).message}
                  </p>
                ) : null}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        {/* Notes timeline */}
        {isLoading ? (
          <NotesSkeleton />
        ) : sorted.length === 0 ? (
          <EmptyState
            icon="edit_note"
            title="Keine Notizen"
            description="Notizen zu dieser Person sind noch nicht erfasst. Klicke auf 'Neue Notiz' oben rechts, um die erste anzulegen."
          />
        ) : (
          <motion.ul
            variants={listVariants}
            initial="hidden"
            animate="show"
            className="space-y-3 list-none"
          >
            <AnimatePresence initial={false}>
              {sorted.map(n => (
                <motion.li
                  key={n.id}
                  variants={noteVariants}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                  layout
                  className="rounded-2xl border border-outline-variant/40 bg-surface-container-lowest p-4"
                >
                  <NoteCard
                    note={n}
                    onDelete={() => deleteNote.mutate(n.id)}
                    isDeleting={deleteNote.isPending && deleteNote.variables === n.id}
                  />
                </motion.li>
              ))}
            </AnimatePresence>
          </motion.ul>
        )}
      </div>
    </HrSection>
  )
}

function NoteCard({ note, onDelete, isDeleting }: { note: PersonNote; onDelete: () => void; isDeleting: boolean }) {
  return (
    <div className="flex items-start gap-3">
      <div
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide ring-1 ${NOTE_KIND_CLASS[note.kind]}`}
        aria-label={`Art: ${NOTE_KIND_LABEL[note.kind]}`}
      >
        {NOTE_KIND_LABEL[note.kind]}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-on-surface whitespace-pre-wrap break-words">{note.text}</p>
        <div className="mt-2 text-[11px] text-on-surface-variant flex items-center gap-2">
          <span>{DATE_FMT.format(new Date(note.createdAt))}</span>
          <span aria-hidden="true">·</span>
          <span>{note.createdBy}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={onDelete}
        disabled={isDeleting}
        className="flex-shrink-0 p-1.5 rounded-lg text-on-surface-variant/60 hover:text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-40"
        aria-label="Notiz archivieren"
        title="Archivieren (Soft-Delete)"
      >
        <span className="material-symbols-outlined text-base" aria-hidden="true">
          {isDeleting ? 'hourglass_empty' : 'archive'}
        </span>
      </button>
    </div>
  )
}

function NotesSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="rounded-2xl border border-outline-variant/40 bg-surface-container-lowest p-4">
          <div className="flex items-start gap-3">
            <div className="h-5 w-16 rounded-full bg-slate-200" />
            <div className="flex-1 space-y-2">
              <div className="h-3 bg-slate-200 rounded w-3/4" />
              <div className="h-3 bg-slate-200 rounded w-1/2" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
